import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { company } from '~/company/Company';
import { cfSrcForImage } from '~/components/CategoryBlocks/utils';
import { Button } from '~/components/isolated/Button';
import { useGlobal } from '~/hooks/useGlobal';
import { useIntercom } from '~/hooks/useIntercom';
import { useStatusBarControl } from '~/hooks/useStatusBarControl';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';

import type { PageHeaderProps } from './interfaces';
import styles from './PageHeader.module.scss';

/**
 * Lightweight page header component for v3 design
 * - Mobile version: displays only the page title
 * - Desktop version: back button (left) + title (center) + optional support button (right)
 */
const PageHeader = React.forwardRef<HTMLHeadingElement, PageHeaderProps>(
  (
    {
      title,
      backgroundImage,
      showBackButton = true,
      backButtonText,
      backButtonPath,
      showSupportButton = false,
      onBackClick,
      className,
    },
    ref,
  ) => {
    const { isMobile } = useGlobal();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { openMessenger } = useIntercom();
    const { pathname } = useLocation();

    // Control status bar color based on scroll position
    useStatusBarControl({
      initialColor: 'light-content',
      scrolledColor: 'dark-content',
    });

    const showBackgroundImage =
      company.hasDeepCatalog && backgroundImage && isMobile;

    const handleBackClick = () => {
      if (onBackClick) {
        onBackClick();
        return;
      }

      // Navigate to backButtonPath if provided
      if (backButtonPath) {
        navigate(backButtonPath);
      }
    };

    const handleSupportClick = () => {
      analyticsService.track(AnalyticsEvent.CONTACT_SUPPORT_BUTTON_CLICKED, {
        from: 'page_header',
      });
      openMessenger();
    };

    const TitleComponent = showBackgroundImage
      ? Typography.Header200Semibold
      : company.match({
          mcosmetic: Typography.Header200Medium,
          default: Typography.Header300Medium,
        });

    // Mobile version - only title
    if (isMobile) {
      if (company.name === 'mcosmetic' && pathname === '/shop') return null;

      return (
        <header className={cn(styles.mobileHeader, className)}>
          <div
            style={{
              backgroundImage: showBackgroundImage
                ? `url(${cfSrcForImage(backgroundImage)})`
                : undefined,
            }}
            className={cn(
              showBackgroundImage
                ? 'flex aspect-360/193 items-end justify-center rounded-b-6.5 bg-center bg-cover bg-no-repeat pb-8 before:absolute before:inset-0 before:rounded-b-[inherit] before:bg-black/30'
                : styles.mobileContent,
              company.match({
                mcosmetic: 'min-h-auto! pb-1!',
              }),
            )}
          >
            <TitleComponent
              className={cn(
                styles.mobileTitle,
                company.isMarketplaceVariant && styles.mobileTitleMarketplace,
                company.match({
                  mcosmetic: 'pb-0!',
                }),
                showBackgroundImage &&
                  'z-1 pb-[6.6%]! text-graphic-regular-white!',
              )}
            >
              {title}
            </TitleComponent>
          </div>
        </header>
      );
    }

    // Desktop version - back button + title + support button
    return (
      <header ref={ref} className={cn(styles.desktopHeader, className)}>
        <div className={styles.desktopContent}>
          {/* Left section - back button */}
          <div className={styles.leftSection}>
            {showBackButton && (
              <Button
                className={styles.backButton}
                startIcon="arrow"
                iconSize={24}
                onClick={handleBackClick}
                variant="secondary"
                size="M"
                aria-label="Go back"
              >
                {backButtonText && (
                  <Typography.Text200Regular
                    as="span"
                    className={styles.backButtonText}
                  >
                    {backButtonText}
                  </Typography.Text200Regular>
                )}
              </Button>
            )}
          </div>

          {/* Center section - title */}
          <div className={styles.centerSection}>
            <TitleComponent className={styles.desktopTitle}>
              {title}
            </TitleComponent>
          </div>

          {/* Right section - support button */}
          <div className={styles.rightSection}>
            {showSupportButton && (
              <Button
                className={styles.supportButton}
                startIcon="support-3.1"
                iconSize={16}
                onClick={handleSupportClick}
                variant="brand"
                size="M"
                aria-label="Contact support"
              >
                <Typography.Text200Semibold
                  as="span"
                  className={styles.desktopTitle}
                >
                  {t('contactSupport')}
                </Typography.Text200Semibold>
              </Button>
            )}
          </div>
        </div>
      </header>
    );
  },
);
PageHeader.displayName = 'PageHeader';

export default observer(PageHeader);
